import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app/App";
import "./styles/global.css";

// Diagnostics only — never mutate the DOM here. React's ErrorBoundary
// handles render-time failures; this is just a console signal for errors
// that happen outside React's reach (e.g. in a Telegram bridge callback).
window.addEventListener("error", (event) => {
  console.error("[startup] uncaught error", event.error ?? event.message);
});
window.addEventListener("unhandledrejection", (event) => {
  console.error("[startup] unhandled promise rejection", event.reason);
});

// Deliberately no Telegram handshake here. Real production traces from
// Telegram Desktop and Telegram iOS both showed execution stopping
// immediately after an earlier version of this file sent a readiness
// signal at this point, before the app's own UI existed. Telegram is now
// told "ready" only via the official bridge, from WelcomeScreen once it has
// actually painted — see ./telegram/readySignal.ts.

/**
 * Renders a minimal, dependency-free fallback directly into the DOM. Used
 * when React itself fails to mount (not a React error — those go through
 * ErrorBoundary), or when bootstrap never completes within a reasonable
 * time (the boot watchdog below) — the two cases where JSX/React state
 * can't be trusted to work or to have run at all.
 */
function renderFatalFallback(container: HTMLElement, error: unknown): void {
  console.error("[startup] fatal mount error", error);
  container.innerHTML = "";

  const wrap = document.createElement("div");
  wrap.style.cssText =
    "display:flex;flex-direction:column;align-items:center;justify-content:center;gap:24px;min-height:100dvh;width:100%;padding:32px;text-align:center;font-family:system-ui,sans-serif;background:#15130f;color:#f2ede2;box-sizing:border-box;";

  const title = document.createElement("div");
  title.textContent = "ALETHEIA";
  title.style.cssText = "font-size:22px;letter-spacing:0.02em;";

  const message = document.createElement("p");
  message.textContent = "Не удалось загрузить ALETHEIA.";
  message.style.cssText = "color:#8f8879;max-width:320px;margin:0;font-size:15px;line-height:1.5;";

  const button = document.createElement("button");
  button.textContent = "Повторить";
  button.style.cssText =
    "background:#c8a464;color:#1a1712;border:none;border-radius:10px;padding:14px 24px;font-weight:600;font-size:14px;letter-spacing:0.04em;text-transform:uppercase;max-width:240px;width:100%;cursor:pointer;";
  button.addEventListener("click", () => window.location.reload());

  const openInBrowser = document.createElement("a");
  openInBrowser.textContent = "Открыть в браузере";
  openInBrowser.href = "https://aletheia-web-7zk.pages.dev";
  openInBrowser.target = "_blank";
  openInBrowser.rel = "noopener noreferrer";
  openInBrowser.style.cssText = "color:#c8a464;font-size:13px;text-decoration:underline;letter-spacing:0.02em;";

  wrap.append(title, message, button, openInBrowser);
  container.appendChild(wrap);
}

function mountApp(container: HTMLElement): void {
  try {
    const root = createRoot(container);
    root.render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  } catch (error) {
    renderFatalFallback(container, error);
    return;
  }

  // Boot watchdog: if React's first commit never replaces the static
  // placeholder (a truly stuck bootstrap that didn't throw synchronously —
  // the one case renderFatalFallback's own try/catch above can't see),
  // never leave the screen permanently black/blank.
  const BOOT_TIMEOUT_MS = 8000;
  window.setTimeout(() => {
    if (container.querySelector("#boot-fallback")) {
      renderFatalFallback(container, new Error("Bootstrap did not complete within the expected time"));
    }
  }, BOOT_TIMEOUT_MS);
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container (#root) not found");
}

mountApp(container);
